import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import { getServerUri } from "../modules/uriUtility";

/**
 * Look at the dashboard as another company.
 *
 * Switching changes the company behind versions, bases, media, agent credit and
 * build output all at once, and `home.js` holds a lot of state derived from all
 * of those. Rather than trying to invalidate each piece, the page is reloaded:
 * the server has already stored the choice, so the reload comes back as the
 * other company. It is the honest option, and switching is a rare, deliberate
 * action.
 *
 * Renders nothing unless the user actually has somewhere to switch to.
 */

// Lives in Account Info, under the company name. It needs ~180px, so it must
// not be put in the 71px game rail — it would push the game list out of view.
const useStyles = makeStyles(() => ({
  selector: {
    minWidth: "180px",
    marginTop: "0.25rem",
  },
  input: {
    fontSize: "0.9rem",
  },
}));

export default function SelectorCompany(props) {
  const classes = useStyles();
  const [isSwitching, setIsSwitching] = useState(false);

  const targets = props.switchTargets || [];
  const homeCompanyID = localStorage.getItem("user.homeCompanyID");
  const hasHome = targets.some((company) => String(company._id) === String(homeCompanyID));
  const options = hasHome || !homeCompanyID
    ? targets
    : [{ _id: homeCompanyID, name: localStorage.getItem("user.companyName") || "My company" }, ...targets];

  if (options.length < 2) {
    return null;
  }

  const switchTo = (companyID) => {
    if (isSwitching || String(companyID) === String(props.currentCompanyID)) {
      return;
    }
    setIsSwitching(true);
    fetch(getServerUri("company/switch"), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        accessToken: localStorage.getItem("session.accessToken"),
        companyID: String(companyID),
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          setIsSwitching(false);
          props.createSnackbar("Could not switch company.", "error", false);
          return;
        }
        // The selected game/base belong to the company being left; clearing them
        // stops the reload from asking for a base the new company may not hold.
        localStorage.removeItem("selectedGameID");
        localStorage.removeItem("selectedBaseID");
        localStorage.removeItem("selectedVersionID");
        window.location.reload();
      })
      .catch(() => {
        setIsSwitching(false);
        props.createSnackbar("Could not switch company.", "error", false);
      });
  };

  return (
    <TextField
      id="selectorCompany"
      select
      size="small"
      label="Current company"
      helperText="Switch to see the dashboard as this company."
      disabled={isSwitching}
      className={classes.selector}
      InputProps={{ classes: { input: classes.input } }}
      value={String(props.currentCompanyID || "")}
      onChange={(event) => switchTo(event.target.value)}
    >
      {options.map((company) => (
        <MenuItem key={String(company._id)} value={String(company._id)}>
          {company.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
